import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { BiLeftArrowAlt, BiRightArrowAlt } from "react-icons/bi";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { AI_BUG_TYPES, getAiDrafts } from "../../../api/aiContent.service";
import { approveAiContent, rejectAiContent } from "../../../app/aiContent/aiContent.slice";
import CardPreview from "../../../components/CardPreview";
import { getAiItemId, getAiItemTitle, getAiItemTranslation, isAiItemPremium } from "../helpers";

const TAKE = 20;

const readQuery = (search) => {
  const q = new URLSearchParams(search || "");
  const type = q.get("type") === "card" ? "card" : "deck";
  const index = Math.max(0, parseInt(q.get("index"), 10) || 0);
  // Revisión de las cards con glosa repetida: misma cola, otro filtro.
  const collisions = q.get("collisions") === "only" ? "only" : undefined;
  return { type, index, collisions };
};

/**
 * One-by-one review mode for AI drafts — mirrors the prev/next navigation UX of
 * the card editing flow (src/screens/NewCard), but with plain ←/→ keys.
 * Route: /ai-content/review?type=card|deck&index=n
 */
const AiDraftsReview = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { aiContent } = useSelector((state) => state);

  const initial = readQuery(location.search);
  const [type, setType] = useState(initial.type);
  const [collisions] = useState(initial.collisions);
  const [index, setIndex] = useState(initial.index);
  const [queue, setQueue] = useState([]);
  const [total, setTotal] = useState(null); // null = unknown yet
  const [isLoading, setIsLoading] = useState(false);
  const [noMore, setNoMore] = useState(false);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectBugType, setRejectBugType] = useState("");

  const loadingRef = useRef(false);
  const processedIdsRef = useRef(new Set());

  // Keep URL in sync so the position is shareable / survives refresh.
  useEffect(() => {
    history.replace(
      `/ai-content/review?type=${type}&index=${index}${collisions ? "&collisions=only" : ""}`
    );
  }, [type, index, collisions, history]);

  const fetchMore = useCallback(
    async (reset = false) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      setIsLoading(true);
      const loadedCount = reset ? 0 : queue.length;
      const page = Math.floor(loadedCount / TAKE);
      const resp = await getAiDrafts(type, { page, take: TAKE, collisions });
      loadingRef.current = false;
      setIsLoading(false);
      if (!resp || resp.status !== 200 || !resp.data) {
        if (reset) {
          setQueue([]);
          setTotal(0);
        }
        setNoMore(true);
        return;
      }
      const results = Array.isArray(resp.data?.results)
        ? resp.data.results
        : Array.isArray(resp.data)
        ? resp.data
        : [];
      const respTotal = resp.data?.pagination?.total ?? resp.data?.pagination?.count;
      setQueue((prev) => {
        const base = reset ? [] : prev;
        // Dedupe (server pages can shift after approvals/rejections) and skip
        // anything already processed in this session.
        const known = new Set(base.map((item) => String(getAiItemId(item))));
        const fresh = results.filter(
          (item) =>
            !known.has(String(getAiItemId(item))) &&
            !processedIdsRef.current.has(String(getAiItemId(item)))
        );
        return [...base, ...fresh];
      });
      if (typeof respTotal === "number") setTotal(respTotal);
      if (results.length < TAKE) setNoMore(true);
    },
    [type, collisions, queue.length]
  );

  // Load initial page on mount and whenever the type changes.
  // (index is reset to 0 by the tab handler; on mount it comes from the URL)
  useEffect(() => {
    processedIdsRef.current = new Set();
    setQueue([]);
    setTotal(null);
    setNoMore(false);
    fetchMore(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Lazy-load the next page as the user approaches the end of the loaded queue.
  useEffect(() => {
    const known = total ?? Infinity;
    if (!noMore && queue.length > 0 && index >= queue.length - 3 && queue.length < known) {
      fetchMore();
    }
  }, [index, queue.length, total, noMore, fetchMore]);

  const safeIndex = Math.min(index, Math.max(0, queue.length - 1));
  const current = queue[safeIndex];
  const displayTotal = total ?? queue.length;

  const goPrev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const goNext = useCallback(
    () => setIndex((i) => Math.min(Math.max(0, queue.length - 1), i + 1)),
    [queue.length]
  );

  // Keyboard ←/→ navigation (disabled while typing or while the dialog is open).
  useEffect(() => {
    const handler = (e) => {
      if (rejectOpen) return;
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [goPrev, goNext, rejectOpen]);

  const removeCurrent = () => {
    if (current) processedIdsRef.current.add(String(getAiItemId(current)));
    setQueue((prev) => prev.filter((_, i) => i !== safeIndex));
    setTotal((prev) => (typeof prev === "number" ? Math.max(0, prev - 1) : prev));
    setIndex((i) => Math.min(i, Math.max(0, queue.length - 2)));
  };

  const handleApprove = async () => {
    if (!current) return;
    const result = await dispatch(approveAiContent({ type, id: getAiItemId(current) }));
    if (approveAiContent.fulfilled.match(result)) {
      toast.success(t("aiDrafts.approvedToast"));
      removeCurrent(); // auto-advance: next item slides into the same index
    } else {
      toast.error(t("toast.errorOccurred"));
    }
  };

  const closeReject = () => {
    setRejectOpen(false);
    setRejectReason("");
    setRejectBugType("");
  };

  const handleConfirmReject = async () => {
    if (!current) return;
    const result = await dispatch(
      rejectAiContent({
        type,
        id: getAiItemId(current),
        reason: rejectReason?.trim() || undefined,
        bugType: type === "card" ? rejectBugType || undefined : undefined,
      })
    );
    if (rejectAiContent.fulfilled.match(result)) {
      toast.success(t("aiDrafts.rejectedToast"));
      closeReject();
      removeCurrent();
    } else {
      toast.error(t("toast.errorOccurred"));
    }
  };

  const deckField = (label, value) => (
    <Stack direction="row" spacing={1} alignItems="baseline">
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value ?? "—"}
      </Typography>
    </Stack>
  );

  const deckCardsCount =
    current?.cards?.length ?? current?.cardsCount ?? current?.numCards ?? current?.nCards;

  return (
    <Box sx={{ maxWidth: 860, mx: "auto" }}>
      <Paper sx={{ borderRadius: 1, overflow: "hidden" }}>
        {/* Header: type tabs + position indicator */}
        <Box
          sx={{
            px: 3,
            pt: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Tabs
            value={type}
            onChange={(_, newType) => {
              setType(newType);
              setIndex(0);
            }}
          >
            <Tab label={t("aiDrafts.decks")} value="deck" sx={{ textTransform: "none", fontWeight: 600 }} />
            <Tab label={t("aiDrafts.cards")} value="card" sx={{ textTransform: "none", fontWeight: 600 }} />
          </Tabs>
          <Typography variant="subtitle1" fontWeight={700}>
            {queue.length > 0 ? `${safeIndex + 1} / ${displayTotal}` : `0 / ${displayTotal || 0}`}
          </Typography>
        </Box>

        {/* Body */}
        {isLoading && queue.length === 0 ? (
          <Box sx={{ py: 10, textAlign: "center" }}>
            <CircularProgress size={32} />
          </Box>
        ) : !current ? (
          <Box sx={{ py: 10, textAlign: "center" }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
              {t("aiDrafts.allReviewed")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t("aiDrafts.empty")}
            </Typography>
            <Button variant="outlined" onClick={() => history.push("/ai-content/drafts")} sx={{ textTransform: "none" }}>
              {t("aiDrafts.backToList")}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "stretch" }}>
            {/* Prev arrow */}
            <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
              <IconButton onClick={goPrev} disabled={safeIndex === 0} aria-label={t("aiDrafts.previous")} size="large">
                <BiLeftArrowAlt size={28} />
              </IconButton>
            </Box>

            {/* Item detail */}
            <Box sx={{ flex: 1, minWidth: 0, py: 3 }}>
              {current?.glossCollision && (
                /* La decisión aquí es si esta glosa se distingue de la publicada:
                   se muestran las dos juntas para no tener que ir a buscarla. */
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 2,
                    border: 1,
                    borderColor: "error.light",
                    bgcolor: (th) => (th.palette.mode === "dark" ? "error.dark" : "error.lighter"),
                    opacity: 0.95,
                  }}
                >
                  <Typography variant="caption" color="error" sx={{ fontWeight: 700 }}>
                    {t("aiDrafts.collisionTitle", "Misma glosa que una card publicada")}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {current?.reviewNote}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.75, fontStyle: "italic" }}>
                    “{getAiItemTranslation(current) || "—"}”
                  </Typography>
                </Box>
              )}

              <Stack direction="row" spacing={0.75} sx={{ mb: 2 }} flexWrap="wrap">
                <Chip label={t("aiDrafts.draft")} size="small" color="warning" variant="outlined" />
                <Chip label={t("aiDrafts.ai")} size="small" color="secondary" variant="outlined" />
                {current?.glossCollision && (
                  <Chip
                    label={t("aiDrafts.collisions", "Glosa repetida")}
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                )}
                {isAiItemPremium(current) && (
                  <Chip
                    label={`${t("common.premium")}${current?.premium?.cost ? ` · ${current.premium.cost}` : ""}`}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                )}
              </Stack>

              {type === "card" ? (
                <>
                  <CardPreview card={current} />
                  {(current?.videoUrl || current?.meaning?.videoUrl) && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary" component="span" sx={{ mr: 1 }}>
                        {t("aiDrafts.video")}:
                      </Typography>
                      <MuiLink
                        href={current?.videoUrl || current?.meaning?.videoUrl}
                        target="_blank"
                        rel="noreferrer"
                        variant="body2"
                      >
                        {current?.videoUrl || current?.meaning?.videoUrl}
                      </MuiLink>
                    </Box>
                  )}
                </>
              ) : (
                <>
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                    {getAiItemTitle(current)}
                  </Typography>
                  {current?.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {current.description}
                    </Typography>
                  )}
                  <Stack spacing={1}>
                    {deckField(t("aiDrafts.level"), current?.level)}
                    {deckField(
                      t("aiDrafts.category"),
                      typeof current?.category === "string"
                        ? current?.category
                        : current?.category?.name || current?.category?.title
                    )}
                    {deckField(t("aiDrafts.numCards"), deckCardsCount)}
                    {deckField(
                      t("common.premium"),
                      isAiItemPremium(current)
                        ? `${t("common.premium")}${current?.premium?.cost ? ` · ${current.premium.cost}` : ""}`
                        : t("common.free")
                    )}
                  </Stack>
                </>
              )}

              {/* Actions */}
              <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  color="success"
                  disableElevation
                  startIcon={<CheckIcon />}
                  disabled={aiContent?.isActing}
                  onClick={handleApprove}
                  sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
                >
                  {t("aiDrafts.approve")}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CloseIcon />}
                  disabled={aiContent?.isActing}
                  onClick={() => setRejectOpen(true)}
                  sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
                >
                  {t("aiDrafts.reject")}
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                {t("aiDrafts.keyboardHint")}
              </Typography>
            </Box>

            {/* Next arrow */}
            <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
              <IconButton
                onClick={goNext}
                disabled={safeIndex >= queue.length - 1}
                aria-label={t("aiDrafts.next")}
                size="large"
              >
                <BiRightArrowAlt size={28} />
              </IconButton>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Reject dialog: optional reason + bug type (cards only) */}
      <Dialog open={rejectOpen} onClose={closeReject} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t("aiDrafts.rejectTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("aiDrafts.confirmReject", { title: getAiItemTitle(current) })}
          </Typography>
          {type === "card" && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="ai-bug-type-label">{t("aiDrafts.bugType")}</InputLabel>
              <Select
                labelId="ai-bug-type-label"
                label={t("aiDrafts.bugType")}
                value={rejectBugType}
                onChange={(e) => setRejectBugType(e.target.value)}
              >
                {AI_BUG_TYPES.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`aiDrafts.bugTypes.${value}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label={t("aiDrafts.rejectReason")}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeReject}>{t("common.cancel")}</Button>
          <Button variant="contained" color="error" disabled={aiContent?.isActing} onClick={handleConfirmReject}>
            {t("aiDrafts.reject")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AiDraftsReview;
